import {Component, Inject, Input, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {RowDoubleClickedEvent, RowNode} from 'ag-grid-community';
import {cloneDeep} from 'lodash-es';
import {combineLatest, takeUntil} from 'rxjs';
import {BaseComponent} from '../../../base-component';
import {AssertionUtils} from '../../../common/utils/assertion-utils';
import {GridOptionsBuilderFactoryService} from '../../../grids/grid-options-builder-factory.service';
import {TranslateService} from '../../../translation/translate.service';
import {FooterConstants} from '../../footer/footer.constants';
import {ContentSwitcherNavigationInput} from '../../navigation-helper-prototype/content-switcher-navigation-input.interface';
import {NavigationHelperPrototypeService} from '../../navigation-helper-prototype/navigation-helper-prototype.service';
import {CrudGridContextService} from '../crud-grid-context-service/crud-grid.context.service';
import {CrudOverviewDataConfig} from '../interfaces/crud-overview-data-config.interface';

@Component({
  templateUrl: './crud-object-selection-dialog.component.html',
  styleUrls: ['./crud-object-selection-dialog.component.scss']
})
export class CrudObjectSelectionDialogComponent extends BaseComponent implements OnInit {
  @Input() protected config: CrudOverviewDataConfig<unknown> = {};
  @Input() protected newClickedEntry: ContentSwitcherNavigationInput;
  @Input() protected canShowGridActions = true;
  @Input() protected canShowFooterActions = true;

  protected showSelected: string;
  protected selectedItemCount = 0;
  protected gridReady = false;
  protected readonly CHOOSE_BUTTON_CLASS_NAME = FooterConstants.CHOOSE_BUTTON_CLASS_NAME;

  public constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private readonly translate: TranslateService,
    protected readonly gridContext: CrudGridContextService,
    private readonly dialogRef: MatDialogRef<CrudObjectSelectionDialogComponent>,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService
  ) {
    super();

    if (!AssertionUtils.isNullOrUndefined(data.config)) {
      this.config = data.config;
    }
  }

  public ngOnInit(): void {
    this.initialiseGrid();

    this.setButtons();

    this.config?.rowActionComplete?.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((data: any) => {
      this.setDataAndInitialiseGridAgain(data);
    });
  }

  public initialiseGrid(): void {
    this.config.gridOptions.popupParent = document.querySelector('.mat-mdc-dialog-container');
    this.showSelected = this.translate.instant('ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.ONLY_SHOW_SELECTED') + ':';
    this.config.dataObservable?.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((data: any[]) => (this.config.rowData = data));

    const gridReadyObservable = this.gridContext.gridReady.asObservable();

    this.setButtons();

    if (this.gridReady) {
      this.setSelectedObject();
    }

    gridReadyObservable.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.gridReady = true;

      this.setSelectedObject();
    });

    this.gridContext.rowDoubleClicked
      .asObservable()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((event: RowDoubleClickedEvent) => {
        this.config.selectedObjects = [event.data];
        this.navigationHelper.navigateBack([event.data]);
      });
  }

  public get getGridSelectedRows(): number {
    return this.gridContext?.grid?.api?.getSelectedRows()?.length ?? 0;
  }

  protected closeDialog(): void {
    this.navigationHelper.navigateBack();
  }

  public back(event: MouseEvent): void {
    AssertionUtils.isNullOrUndefined(this.config.withBackClicked) ? this.dialogRef?.close() : this.config.withBackClicked(event);
  }

  public canShowOnlyShowSelectedToggle(): boolean {
    return this.hasMultiRowSelectionEnabled() && (this.selectedItemCount !== 0 || this.config.onlyShowSelected);
  }

  protected hasMultiRowSelectionEnabled(): boolean {
    return (this.config.gridOptions?.rowSelection as any)?.mode === 'multiRow';
  }

  protected select(): void {
    const data = this.gridContext.grid?.api?.getSelectedRows() ?? [];

    AssertionUtils.isNullOrUndefined(this.config.withConfirmClicked) ? this.dialogRef?.close(data) : this.config.withConfirmClicked(data);
  }

  protected setButtons(): void {
    if (AssertionUtils.isNullOrUndefined(this.config.withNewClicked) && !AssertionUtils.isNullOrUndefined(this.newClickedEntry)) {
      this.config.withNewClicked = (): void => {
        this.navigationHelper
          .navigateWithContentSwitcher(cloneDeep(this.newClickedEntry))
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((data: any) => {
            this.setDataAndInitialiseGridAgain(data);
          });
      };
    }

    if (AssertionUtils.isNullOrUndefined(this.config.withBackClicked)) {
      this.config.withBackClicked = (): void => this.navigationHelper.navigateBack();
    }

    if (AssertionUtils.isNullOrUndefined(this.config.withConfirmClicked)) {
      this.config.withConfirmClicked = (data: any[]): void => {
        this.navigationHelper.navigateBack(data);
      };
    }
  }

  protected setSelectedObject(): void {
    this.gridOptionsBuilderFactoryService
      .getBuilderFromExistingGridApi(this.gridContext.grid.api)
      .withAutomaticRowSelection()
      .withAutomaticButtonFocusOnRowSelection(`[id="${this.dialogRef.id}"] .${FooterConstants.CHOOSE_BUTTON_CLASS_NAME}`);

    combineLatest([this.gridContext.grid.firstDataRendered, this.gridContext.grid.rowDataUpdated])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        setTimeout(() => {
          if (this.config.selectedObjects?.length > 0) {
            this.gridContext.grid.api.forEachNode((node: RowNode) => {
              node.setSelected(this.config.selectedObjects?.some((selectedObject: any) => selectedObject?.id === node.data?.id));
            });
          } else {
            this.config.onlyShowSelected = false;
            this.gridContext.grid.api.forEachNode((node: RowNode) => {
              node.setSelected(false);
            });
          }
        });
      });

    this.gridContext.grid.rowSelected?.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.config.selectedObjects = this.gridContext.grid.api?.getSelectedRows();
      this.selectedItemCount = this.gridContext.grid.api.getSelectedRows()?.length ?? 0;

      if (this.config.onlyShowSelected) {
        this.toggleShowOnlySelected(this.selectedItemCount > 0);
      }
    });
  }

  protected setDataAndInitialiseGridAgain(data: any): void {
    if (!AssertionUtils.isNullOrUndefined(data)) {
      if (this.hasMultiRowSelectionEnabled()) {
        this.config.selectedObjects.push(data);
      } else {
        this.config.selectedObjects = [data];
      }
    }

    setTimeout(() => this.gridContext.setCurrentGrid.emit());
    this.initialiseGrid();
  }

  protected toggleShowOnlySelected(checked: boolean): void {
    this.config.onlyShowSelected = checked;

    if (this.config.gridOptions.rowModelType === 'serverSide') {
      this.gridContext.grid.api.onFilterChanged();
    } else {
      checked ? this.gridContext.grid.api.setGridOption('rowData', this.gridContext.grid.api?.getSelectedRows()) : this.gridContext.grid.api.setGridOption('rowData', this.config.rowData);
    }
  }
}
